import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CrmBadge, CrmButton } from '../components/ui';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import {
  completeOrderPickup,
  dispatchOrder,
  downloadOrderKitchenCopyPdf,
  getOrder,
  markOrderDelivered,
  markOrderPickupReady,
  markOrderReady,
  releaseOrder,
  startOrderFulfilment,
} from '../services/orderService';
import { formatCurrency } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateFormatter';
import { formatStatusLabel, normalizeStatusValue } from '../utils/statusFormatter';
import { useSettings } from '../context/SettingsContext';
import './OrderDetail.scss';

const parseJson = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return {}; }
};

const tone = (value) => {
  const status = normalizeStatusValue(value);
  if (['paid','completed','ready','ready_for_pickup'].includes(status)) return 'success';
  if (['failed','cancelled'].includes(status)) return 'danger';
  if (['pending','awaiting_payment','awaiting_confirmation','partially_paid'].includes(status)) return 'warning';
  if (['confirmed','in_fulfilment','processing','out_for_delivery','shipped'].includes(status)) return 'info';
  return 'neutral';
};

const sourceLabel = (order) => {
  const source = String(order?.source || order?.source_type || '').toUpperCase();
  if (source === 'WEBSITE' || source.includes('FRONTEND')) return 'Website';
  if (source.includes('QUOT')) return 'Quotation';
  return source ? source.replace(/_/g, ' ') : 'Manual';
};

const displayAddress = (snapshot) => [snapshot.address, snapshot.area, snapshot.city, snapshot.state, snapshot.pincode].filter(Boolean).join(', ') || '—';

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [kitchenCopyLoading, setKitchenCopyLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOrder(id));
      setError('');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to load order.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (key, fn, successMessage) => {
    setActionLoading(key);
    try {
      await fn(id);
      setNotification({ open: true, message: successMessage, severity: 'success' });
      await load();
    } catch (requestError) {
      setNotification({ open: true, message: requestError?.response?.data?.error || requestError?.message || 'Order action failed.', severity: 'error' });
    } finally {
      setActionLoading('');
    }
  };

  const downloadKitchenCopy = async () => {
    if (!data?.order?.id || kitchenCopyLoading) return;
    setKitchenCopyLoading(true);
    try {
      await downloadOrderKitchenCopyPdf(data.order.id, data.order);
    } catch (requestError) {
      setNotification({ open: true, message: requestError?.message || 'Failed to download kitchen copy.', severity: 'error' });
    } finally {
      setKitchenCopyLoading(false);
    }
  };

  const order = data?.order || {};
  const fulfilment = data?.fulfilment || null;
  const profile = data?.profile || null;
  const artifacts = data?.artifacts || {};
  const billing = useMemo(() => parseJson(order.billing_snapshot), [order.billing_snapshot]);
  const shipping = useMemo(() => parseJson(order.shipping_snapshot), [order.shipping_snapshot]);
  const event = useMemo(() => parseJson(order.event_snapshot), [order.event_snapshot]);
  const fulfilmentStatus = String(fulfilment?.status || '').toUpperCase();
  const fulfilmentMode = String(fulfilment?.fulfilment_mode || order.fulfilment_mode || '').toUpperCase();
  const profileScope = useMemo(() => {
    const type = String(profile?.scope_type || '').toUpperCase();
    const names = {
      LEGAL_ENTITY: order.legal_entity_name,
      BRAND: order.brand_name,
      BUSINESS_UNIT: order.business_unit_name,
      SALES_CHANNEL: order.sales_channel_name,
      STOREFRONT: order.storefront_name,
      GLOBAL: 'Global',
    };
    return { type: type || 'GLOBAL', name: names[type] || (type === 'GLOBAL' ? 'Global' : `Record #${profile?.scope_id || '—'}`) };
  }, [order.brand_name, order.business_unit_name, order.legal_entity_name, order.sales_channel_name, order.storefront_name, profile?.scope_id, profile?.scope_type]);
  const groupedOrderItems = useMemo(() => {
    const groups = new Map();
    (data?.items || []).forEach((item) => {
      const metadata = parseJson(item.metadata_snapshot);
      const pkg = metadata.quotation_package || null;
      const key = pkg?.key || pkg?.name || '__shared__';
      if (!groups.has(key)) groups.set(key, { key, name: pkg?.name || 'Shared / General Items', pax: pkg?.pax || null, items: [] });
      groups.get(key).items.push(item);
    });
    return Array.from(groups.values()).sort((a,b) => (a.key === '__shared__' ? 1 : b.key === '__shared__' ? -1 : 0));
  }, [data?.items]);


  const primaryAction = useMemo(() => {
    if (!order.id || ['COMPLETED','CANCELLED'].includes(String(order.status || '').toUpperCase())) return null;
    if (!fulfilment) return { key: 'release', label: 'Release to Fulfilment', fn: releaseOrder, message: 'Order released to fulfilment.' };
    if (fulfilmentStatus === 'NOT_STARTED') return { key: 'start', label: 'Start Fulfilment', fn: startOrderFulfilment, message: 'Fulfilment started.' };
    if (fulfilmentMode === 'PICKUP' && ['PROCESSING','READY'].includes(fulfilmentStatus)) return { key: 'pickup-ready', label: 'Ready for Pickup', fn: markOrderPickupReady, message: 'Order marked ready for pickup.' };
    if (fulfilmentMode === 'PICKUP' && fulfilmentStatus === 'READY_FOR_PICKUP') return { key: 'pickup-complete', label: 'Complete Pickup', fn: completeOrderPickup, message: 'Pickup completed.' };
    if (fulfilmentStatus === 'PROCESSING') return { key: 'ready', label: 'Mark Ready', fn: markOrderReady, message: 'Order marked ready.' };
    if (['DELIVERY','SHIPPING'].includes(fulfilmentMode) && fulfilmentStatus === 'READY') return { key: 'dispatch', label: fulfilmentMode === 'SHIPPING' ? 'Mark Shipped' : 'Dispatch Order', fn: dispatchOrder, message: fulfilmentMode === 'SHIPPING' ? 'Order marked shipped.' : 'Order dispatched.' };
    if (['OUT_FOR_DELIVERY','SHIPPED'].includes(fulfilmentStatus)) return { key: 'delivered', label: fulfilmentMode === 'SHIPPING' ? 'Mark Delivered' : 'Complete Delivery', fn: markOrderDelivered, message: 'Order completed.' };
    return null;
  }, [fulfilment, fulfilmentMode, fulfilmentStatus, order.id, order.status]);

  if (loading) return <div className="order-detail-state">Loading order…</div>;
  if (error) return <div className="order-detail-state is-error"><strong>Could not load order</strong><span>{error}</span><CrmButton onClick={() => navigate('/orders')}>Back to Orders</CrmButton></div>;

  return (
    <div className="order-detail-page">
      <header className="order-detail-header">
        <div className="order-detail-header__left">
          <CrmButton variant="tertiary" iconOnly aria-label="Back to orders" onClick={() => navigate('/orders')}><ArrowBackIcon fontSize="small" /></CrmButton>
          <div>
            <div className="order-detail-header__title-row">
              <h1>{order.order_number || `Order #${order.id}`}</h1>
              <CrmBadge tone={tone(order.status)}>{formatStatusLabel(order.status)}</CrmBadge>
              <CrmBadge tone={tone(order.payment_status)}>{formatStatusLabel(order.payment_status)}</CrmBadge>
            </div>
            <p>{order.customer_name || billing.name || 'Customer'} · {sourceLabel(order)} · {formatDate(order.created_at)}</p>
          </div>
        </div>
        <div className="order-detail-header__actions">
          <CrmButton variant="secondary" startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />} loading={kitchenCopyLoading} disabled={kitchenCopyLoading} onClick={downloadKitchenCopy}>
            Kitchen Copy PDF
          </CrmButton>
          {primaryAction ? (
            <CrmButton variant="primary" startIcon={<PlayArrowOutlinedIcon fontSize="small" />} loading={actionLoading === primaryAction.key} onClick={() => runAction(primaryAction.key, primaryAction.fn, primaryAction.message)}>
              {primaryAction.label}
            </CrmButton>
          ) : null}
        </div>
      </header>

      <div className="order-detail-metrics">
        <div><span>Order Total</span><strong>{formatCurrency(order.grand_total || order.total_amount || 0, settings?.currency_code)}</strong></div>
        <div><span>Fulfilment</span><strong>{formatStatusLabel(fulfilment?.status || 'Not started')}</strong><small>{formatStatusLabel(fulfilmentMode || 'Not assigned')}</small></div>
        <div><span>Storefront</span><strong>{order.storefront_name || '—'}</strong><small>{order.sales_channel_name || sourceLabel(order)}</small></div>
        <div><span>Profile</span><strong>{profile?.name || order.fulfilment_profile_name || 'Not resolved'}</strong><small>{profile?.release_condition ? `Releases on: ${formatStatusLabel(profile.release_condition)}` : '—'}</small></div>
      </div>

      <div className="order-detail-grid">
        <main className="order-detail-main">
          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Order Items</h2><p>Commercial lines captured on the unified Order.</p></div><span>{data?.items?.length || 0} lines</span></div>
            <div className="order-items-table-wrap">
              <table className="order-items-table">
                <thead><tr><th>Item</th><th>SKU / Variant</th><th>Qty</th><th>Rate</th><th>Tax</th><th>Total</th></tr></thead>
                <tbody>
                  {groupedOrderItems.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="order-items-package-row"><td colSpan="6"><strong>{group.name}</strong>{group.pax ? <small>{group.pax} PAX</small> : null}</td></tr>
                      {group.items.map((item) => (
                        <tr key={item.id} className={String(item.line_type).toUpperCase() === 'OTHER' ? 'is-component' : ''}>
                          <td><strong>{item.product_name_snapshot || 'Item'}</strong>{item.description_snapshot ? <small>{item.description_snapshot}</small> : null}</td>
                          <td>{[item.sku_snapshot, item.variant_name_snapshot].filter(Boolean).join(' · ') || '—'}</td>
                          <td>{Number(item.quantity || 0).toLocaleString('en-IN')}</td>
                          <td>{formatCurrency(item.unit_price || 0, settings?.currency_code)}</td>
                          <td>{Number(item.gst_rate || 0) ? `${Number(item.gst_rate)}%` : '—'}</td>
                          <td><strong>{formatCurrency(item.line_total || 0, settings?.currency_code)}</strong></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {!data?.items?.length ? <tr><td colSpan="6" className="order-items-table__empty">No order lines.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <div className="order-detail-total">
              <span>Subtotal<strong>{formatCurrency(order.subtotal || 0, settings?.currency_code)}</strong></span>
              <span className="is-grand">Grand Total<strong>{formatCurrency(order.grand_total || order.total_amount || 0, settings?.currency_code)}</strong></span>
            </div>
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Customer & Fulfilment</h2><p>Immutable checkout/address snapshots and order instructions.</p></div></div>
            <div className="order-info-grid">
              <div><small>Customer</small><strong>{order.customer_name || billing.name || '—'}</strong><span>{billing.email || shipping.email || '—'}</span><span>{billing.phone || shipping.phone || '—'}</span>{order.customer_gst ? <span>GST: {order.customer_gst}</span> : null}</div>
              <div><small>Billing Address</small><strong>{billing.name || order.customer_name || '—'}</strong><span>{displayAddress(billing)}</span></div>
              <div><small>Shipping / Delivery</small><strong>{shipping.name || order.customer_name || '—'}</strong><span>{displayAddress(shipping)}</span></div>
              <div><small>Fulfilment Details</small><strong>{formatStatusLabel(fulfilmentMode || 'Not assigned')}</strong><span>{event.date ? `Date: ${formatDate(event.date)}` : 'Date: —'}</span><span>{event.time ? `Time: ${event.time}` : 'Time: —'}</span></div>
            </div>
            {order.delivery_instructions || order.notes ? <div className="order-detail-notes">{order.delivery_instructions ? <p><strong>Delivery:</strong> {order.delivery_instructions}</p> : null}{order.notes ? <p><strong>Notes:</strong> {order.notes}</p> : null}</div> : null}
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Activity</h2><p>Lifecycle transitions recorded by the Order engine.</p></div></div>
            <div className="order-activity-list">
              {(data?.activity || []).map((entry) => (
                <div key={entry.id} className="order-activity-row">
                  <span className="order-activity-row__dot" />
                  <div><strong>{formatStatusLabel(entry.activity_type)}</strong><span>{entry.from_status && entry.to_status ? `${formatStatusLabel(entry.from_status)} → ${formatStatusLabel(entry.to_status)}` : entry.notes || 'Order event'}</span></div>
                  <small>{formatDate(entry.created_at)}{entry.actor_name ? ` · ${entry.actor_name}` : ''}</small>
                </div>
              ))}
              {!data?.activity?.length ? <div className="order-detail-empty">No activity recorded yet.</div> : null}
            </div>
          </section>
        </main>

        <aside className="order-detail-side">
          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Operational Flow</h2><p>The unified Order is the master record; KOT drives kitchen execution and Delivery follows fulfilment.</p></div></div>
            <div className="order-flow-list">
              <div className={profile?.requires_kot ? 'is-required' : ''}><RestaurantMenuOutlinedIcon fontSize="small" /><span><strong>KOT</strong><small>{profile?.requires_kot ? 'Kitchen execution ticket created on release' : 'Not required by this profile'}</small></span>{artifacts.kot_id ? <CrmButton size="small" variant="tertiary" onClick={() => navigate(`/kots?range=all&focusKotId=${artifacts.kot_id}`)}>Open</CrmButton> : <em>—</em>}</div>
              <div className={profile?.requires_delivery || fulfilmentMode === 'DELIVERY' ? 'is-required' : ''}><LocalShippingOutlinedIcon fontSize="small" /><span><strong>Delivery</strong><small>{profile?.requires_delivery || fulfilmentMode === 'DELIVERY' ? 'Created when dispatch needs it' : 'Not required'}</small></span>{artifacts.delivery_id ? <CrmButton size="small" variant="tertiary" onClick={() => navigate(`/deliveries?focusDeliveryId=${artifacts.delivery_id}`)}>Open</CrmButton> : <em>—</em>}</div>
              <div className="is-required"><ReceiptLongOutlinedIcon fontSize="small" /><span><strong>Tax Invoice</strong><small>Generated by order release policy</small></span>{artifacts.invoice_id ? <CrmButton size="small" variant="tertiary" onClick={() => navigate(`/invoices/${artifacts.invoice_id}`)}>Open</CrmButton> : <em>—</em>}</div>
            </div>
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Resolved Fulfilment Profile</h2><p>This is the exact configuration controlling this Order's operational handoffs.</p></div></div>
            {profile ? (
              <dl className="order-context-list">
                <div><dt>Profile</dt><dd>{profile.name || profile.code || '—'}</dd></div>
                <div><dt>Matched scope</dt><dd>{formatStatusLabel(profileScope.type)} · {profileScope.name}</dd></div>
                <div><dt>Flow origin</dt><dd>{formatStatusLabel(profile.entry_stage || 'ORDER')}</dd></div>
                <div><dt>Release trigger</dt><dd>{formatStatusLabel(profile.release_condition || '—')}</dd></div>
                <div><dt>Kitchen ticket</dt><dd>{profile.requires_kot ? 'Create KOT on release' : 'No KOT required'}</dd></div>
                <div><dt>Physical fulfilment</dt><dd>{formatStatusLabel(profile.fulfilment_requirement || 'NOT_USED')}</dd></div>
                <div><dt>Default type</dt><dd>{formatStatusLabel(profile.default_fulfilment_mode || '—')}</dd></div>
                <div><dt>Staff override</dt><dd>{profile.allow_manual_release ? 'Allowed' : 'Not allowed'}</dd></div>
              </dl>
            ) : <div className="order-detail-empty">No Fulfilment Profile resolved for this Order.</div>}
            <CrmButton size="small" variant="secondary" onClick={() => navigate('/settings?section=fulfilmentProfiles')}>Open Fulfilment Settings</CrmButton>
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Related Records</h2><p>Jump to the commercial and operational records generated from this Order.</p></div></div>
            <div className="order-flow-list">
              {data?.related?.quotation?.id ? <div><span><strong>Quotation</strong><small>{data.related.quotation.quotation_number || `Quotation #${data.related.quotation.id}`}</small></span><CrmButton size="small" variant="tertiary" onClick={() => navigate(`/quotations/${data.related.quotation.id}`)}>Open</CrmButton></div> : null}
              {artifacts.kot_id ? <div><span><strong>KOT</strong><small>Kitchen execution</small></span><CrmButton size="small" variant="tertiary" onClick={() => navigate(`/kots?range=all&focusKotId=${artifacts.kot_id}`)}>Open</CrmButton></div> : null}
              {artifacts.delivery_id ? <div><span><strong>Delivery</strong><small>Delivery execution</small></span><CrmButton size="small" variant="tertiary" onClick={() => navigate(`/deliveries?range=all&focusDeliveryId=${artifacts.delivery_id}`)}>Open</CrmButton></div> : null}
              {(data?.related?.proforma_invoices || []).map((doc) => <div key={`proforma-${doc.id}`}><span><strong>Proforma Invoice</strong><small>{doc.proforma_number || `#${doc.id}`}</small></span><CrmButton size="small" variant="tertiary" onClick={() => navigate(`/proforma-invoices/${doc.id}`)}>Open</CrmButton></div>)}
              {(data?.related?.invoices || []).map((doc) => <div key={`invoice-${doc.id}`}><span><strong>Tax Invoice</strong><small>{doc.invoice_number || `#${doc.id}`}</small></span><CrmButton size="small" variant="tertiary" onClick={() => navigate(`/invoices/${doc.id}`)}>Open</CrmButton></div>)}
              {!data?.related?.quotation?.id && !artifacts.kot_id && !artifacts.delivery_id && !(data?.related?.proforma_invoices || []).length && !(data?.related?.invoices || []).length ? <div className="order-detail-empty">No related records yet.</div> : null}
            </div>
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Commercial Context</h2><p>No raw storefront or structure IDs.</p></div></div>
            <dl className="order-context-list">
              <div><dt>Legal Entity</dt><dd>{order.legal_entity_name || '—'}</dd></div>
              <div><dt>Brand</dt><dd>{order.brand_name || '—'}</dd></div>
              <div><dt>Business Unit</dt><dd>{order.business_unit_name || '—'}</dd></div>
              <div><dt>Sales Channel</dt><dd>{order.sales_channel_name || '—'}</dd></div>
              <div><dt>Storefront</dt><dd>{order.storefront_name || '—'}</dd></div>
            </dl>
          </section>

          <section className="order-detail-card">
            <div className="order-detail-card__head"><div><h2>Payments</h2><p>Payment attempts linked directly to this Order.</p></div></div>
            <div className="order-payment-list">
              {(data?.payments || []).map((payment) => <div key={payment.id}><span><strong>{payment.provider || 'Payment'}</strong><small>{formatDate(payment.created_at)}</small></span><span><CrmBadge tone={tone(payment.status)}>{formatStatusLabel(payment.status)}</CrmBadge><strong>{formatCurrency(payment.amount || 0, payment.currency || settings?.currency_code)}</strong></span></div>)}
              {!data?.payments?.length ? <div className="order-detail-empty">No payment attempts.</div> : null}
            </div>
          </section>

          {String(order.status || '').toUpperCase() === 'COMPLETED' ? <div className="order-detail-complete"><CheckCircleOutlineIcon /><div><strong>Order complete</strong><span>The fulfilment lifecycle has finished.</span></div></div> : null}
        </aside>
      </div>

      <NotificationSnackbar {...notification} onClose={() => setNotification((current) => ({ ...current, open: false }))} />
    </div>
  );
}
